// ══════════════════════════════════════════════════════════════════════════════
// PAGE MA GALERIE - Style harmonisé avec Mon Espace (Liste de tâches)
// AbracadaWorld - Cockpit Particulier
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Image, Plus, Search, Heart, Eye, Trash2, 
  ChevronRight, Clock, X, Upload, Camera, Loader2, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MaGalerie({ onAskAbra }) {
  const { user } = useCurrentUser();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('Tous');
  const [filterCounts, setFilterCounts] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingImage, setViewingImage] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const isDarkMode = false;
  
  // Refs et états pour le scroll des filtres
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);

  const filters = ['Tous', 'Récents', 'Favoris', 'Projets', 'Produits'];
  
  // Gérer le scroll des filtres
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const isDemo = user?.isDemo;
  const DEMO_IMAGES = [];

  useEffect(() => {
    setLoading(true);
    if (isDemo) {
      setImages(DEMO_IMAGES);
      setFilterCounts({ 'Tous': 0, 'Récents': 0, 'Favoris': 0, 'Projets': 0, 'Produits': 0 });
    } else {
      setImages([]);
      setFilterCounts({ 'Tous': 0, 'Récents': 0, 'Favoris': 0, 'Projets': 0, 'Produits': 0 });
    }
    setLoading(false);
  }, [user, isDemo]);

  const handleDelete = async (id) => {
    if (isDemo) {
      toast.error('Crée ton compte pour supprimer des photos !');
      return;
    }
    if (!window.confirm('Supprimer cette image ?')) return;
    setImages(prev => prev.filter(i => i.id !== id));
    toast.success('Image supprimée');
  };

  const handleView = (image) => {
    setViewingImage(image);
  };

  const handleAddPhotos = () => {
    if (isDemo) {
      toast.error('Crée ton compte pour ajouter des photos !');
      return;
    }
    setShowUploadModal(true);
  };

  const filteredImages = images.filter(image => {
    const matchesSearch = image.title?.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    
    switch (activeFilter) {
      case 'Récents': return true; // Could filter by date
      case 'Favoris': return image.isFavorite;
      case 'Projets': return image.category === 'projet';
      case 'Produits': return image.category === 'produit';
      default: return true;
    }
  });

  const totalLikes = images.reduce((acc, i) => acc + (i.likes || 0), 0);
  const totalViews = images.reduce((acc, i) => acc + (i.views || 0), 0);
  const totalImages = filterCounts['Tous'] || images.length || 0;

  return (
    <div className="h-full flex flex-col">
      {/* Header - Style identique à Mes Notes */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h1 className={`text-[16px] sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#1e3a5f]'}`}>
              Ma galerie
            </h1>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Partage tes photos et réalisations avec la communauté
            </p>
          </div>
          <Button 
            onClick={handleAddPhotos}
            data-testid="add-photos-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Ajouter des photos</span>
          </Button>
        </div>

        {/* Stats rapides */}
        <div className="flex items-center gap-4 mt-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
            isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
          }`}>
            <Image size={14} className="text-orange-500" />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {totalImages} photo{totalImages > 1 ? 's' : ''}
            </span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
            isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
          }`}>
            <Heart size={14} className="text-red-500" />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {totalLikes} j'aime
            </span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
            isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
          }`}>
            <Eye size={14} className="text-blue-500" />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {totalViews} vues
            </span>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="mb-3">
        <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher une photo..."
            className={`flex-1 bg-transparent border-0 outline-none text-xs placeholder-gray-400 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
          />
        </div>
      </div>

      {/* Filtres - Style identique à Mes Notes / Mes Messages */}
      <div className="flex-shrink-0 pb-3">
        <div className="relative flex items-center">
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {filters.map((filter) => {
              const count = filterCounts[filter] || 0;
              
              return (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  data-testid={`filter-${filter.toLowerCase()}`}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                    activeFilter === filter
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <span className="flex items-center gap-1">
                    {filter}
                    {count > 0 && (
                      <span className={`min-w-[18px] h-[18px] flex items-center justify-center text-[10px] rounded-full ${
                        activeFilter === filter 
                          ? 'bg-white/25 text-white' 
                          : 'bg-orange-500 text-white'
                      }`}>
                        {count}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          
          {/* Flèche orange discrète */}
          {showScrollHint && (
            <div className="flex-shrink-0 flex items-center pointer-events-none">
              <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
            </div>
          )}
        </div>
      </div>

      {/* Banner démo */}
      {isDemo && (
        <div className={`mb-3 p-3 rounded-xl flex items-center gap-3 ${
          isDarkMode ? 'bg-amber-900/30 border border-amber-700' : 'bg-amber-50 border border-amber-200'
        }`}>
          <Camera size={18} className="text-amber-500" />
          <div>
            <p className={`font-medium text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>Mode Démonstration</p>
            <p className={`text-xs ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>Ces photos sont des exemples. Crée ton compte pour gérer ta propre galerie.</p>
          </div>
        </div>
      )}

      {/* Grille des images */}
      <div className={`flex-1 rounded-2xl overflow-hidden ${
        isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
      }`}>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredImages.length === 0 ? (
          /* État vide - Style harmonisé */
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8 max-w-sm">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                isDarkMode ? 'bg-slate-700' : 'bg-gray-50'
              }`}>
                <Image size={28} className={isDarkMode ? 'text-gray-500' : 'text-gray-300'} />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Ta galerie est vide
              </h3>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Ajoute des photos de tes réalisations, projets ou produits pour les montrer à la communauté.
              </p>
              <Button 
                onClick={handleAddPhotos}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2"
              >
                <Upload size={16} />
                Ajouter ma première photo
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-4 overflow-y-auto h-full">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredImages.map((image) => (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative group aspect-square rounded-xl overflow-hidden bg-gray-100 cursor-pointer"
                  onClick={() => handleView(image)}
                >
                  <img src={image.url} alt={image.title} className="w-full h-full object-cover" />
                  
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleView(image); }}
                      className="p-2 bg-white rounded-full hover:bg-gray-100"
                    >
                      <Eye size={16} className="text-gray-700" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(image.id); }}
                      className="p-2 bg-white rounded-full hover:bg-red-50"
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </button>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                    <p className="text-white text-xs font-medium truncate">{image.title}</p>
                    <div className="flex items-center gap-2 text-white/80 text-[10px] mt-0.5">
                      <span className="flex items-center gap-0.5">
                        <Heart size={10} /> {image.likes || 0}
                      </span>
                      <span className="flex items-center gap-0.5">
                        <Eye size={10} /> {image.views || 0}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {viewingImage && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setViewingImage(null)}
          >
            <button 
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
              onClick={() => setViewingImage(null)}
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <img 
                src={viewingImage.url} 
                alt={viewingImage.title}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              <p className="text-white text-lg font-medium mt-4">{viewingImage.title}</p>
              {viewingImage.description && (
                <p className="text-white/70 mt-1">{viewingImage.description}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Upload */}
      <AnimatePresence>
        {showUploadModal && (
          <UploadModal 
            onClose={() => setShowUploadModal(false)}
            onUpload={(data) => {
              setImages(prev => [{ id: Date.now(), ...data, likes: 0, views: 0 }, ...prev]);
              setShowUploadModal(false);
              toast.success('Photo ajoutée !');
            }}
            isDarkMode={isDarkMode}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Modal d'upload - Style harmonisé
function UploadModal({ onClose, onUpload, isDarkMode }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Sélectionne un fichier image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5 Mo)');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!previewUrl) {
      toast.error('Sélectionne une image');
      return;
    }
    
    setUploading(true);
    await onUpload({
      url: previewUrl,
      title: title || 'Sans titre',
      description
    });
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 md:left-[288px] z-50 flex items-center justify-center p-4 bg-black/60">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={`${isDarkMode ? 'bg-slate-900' : 'bg-white'} rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col`}
      >
        <div className={`px-6 py-4 border-b flex items-center justify-between flex-shrink-0 ${
          isDarkMode ? 'border-slate-700' : 'border-gray-200'
        }`}>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Ajouter une photo</h3>
          <button onClick={onClose} className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-100'}`}>
            <X size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {/* Zone d'upload */}
          {previewUrl ? (
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-gray-200">
              <img src={previewUrl} alt="Aperçu" className="w-full h-full object-cover" />
              <button
                onClick={() => setPreviewUrl(null)}
                className="absolute top-2 right-2 p-2 bg-white/90 rounded-full hover:bg-white shadow"
              >
                <X size={16} className="text-gray-700" />
              </button>
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`aspect-[4/3] border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-colors ${
                isDarkMode 
                  ? 'border-slate-600 hover:border-orange-500 hover:bg-orange-500/10' 
                  : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/50'
              }`}
            >
              <Upload size={40} className="text-gray-400 mb-3" />
              <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Clique pour sélectionner une image</p>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>JPG, PNG, WEBP • Max 5 Mo</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Titre */}
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Titre</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Donne un titre à ta photo..."
              className={`w-full px-4 py-3 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                isDarkMode 
                  ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                  : 'border border-gray-200 text-gray-900'
              }`}
            />
          </div>

          {/* Description */}
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description (optionnel)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Décris ce que tu montres..."
              rows={3}
              className={`w-full px-4 py-3 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none ${
                isDarkMode 
                  ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                  : 'border border-gray-200 text-gray-900'
              }`}
            />
          </div>
        </div>

        <div className={`px-6 py-4 border-t flex justify-end gap-3 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <Button variant="outline" onClick={onClose} disabled={uploading}>
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={uploading || !previewUrl}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {uploading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Envoi...
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                Ajouter
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
